import { DocumentTypesTable } from "@/components/compliance/document-types-table";
import { RefreshIconButton } from "@/components/refresh-icon-button";
import { Switch } from "@/components/ui/switch";
import { canAdminRead, canAdminWrite } from "@/lib/auth";
import { complianceApi } from "@/lib/compliance-api";
import { getAdminDocumentTypesQueryOptions } from "@/lib/compliance-query-keys";
import type { CollectionType } from "@/types/compliance";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod/v4";

const searchParamsSchema = z.object({
  includeInactive: z.boolean().optional().default(true),
});

export const Route = createFileRoute(
  "/(app)/_admin-layout/admin_/compliance/document-types",
)({
  validateSearch: zodValidator(searchParamsSchema),
  loaderDeps: ({ search }) => ({ includeInactive: search.includeInactive }),
  loader: async ({
    context: { queryClient, accessToken, authorities },
    deps,
  }) => {
    if (!canAdminRead(authorities)) return;
    await queryClient.ensureQueryData(
      getAdminDocumentTypesQueryOptions({
        accessToken,
        includeInactive: deps.includeInactive,
      }),
    );
  },
  component: AdminComplianceDocumentTypes,
});

function AdminComplianceDocumentTypes() {
  const context = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const hasReadAccess = canAdminRead(context.authorities);
  const hasWriteAccess = canAdminWrite(context.authorities);

  const documentTypesQuery = useSuspenseQuery(
    getAdminDocumentTypesQueryOptions({
      accessToken: context.accessToken,
      includeInactive: search.includeInactive,
    }),
  );

  const createDocumentTypeMutation = useMutation({
    mutationFn: (payload: {
      code: string;
      displayName: string;
      description?: string | null;
      supporting?: string[];
      collectionType: CollectionType;
    }) =>
      complianceApi.admin.createDocumentType({
        accessToken: context.accessToken,
        ...payload,
      }),
    onSuccess: () => {
      toast.success("Requirement configuration created");
      void queryClient.invalidateQueries({
        queryKey: ["compliance", "admin", "document-types"],
      });
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to create requirement configuration",
      );
    },
  });

  const updateDocumentTypeMutation = useMutation({
    mutationFn: (payload: {
      code: string;
      displayName?: string;
      description?: string | null;
      supporting?: string[];
      collectionType?: CollectionType;
    }) =>
      complianceApi.admin.updateDocumentType({
        accessToken: context.accessToken,
        ...payload,
      }),
    onSuccess: () => {
      toast.success("Requirement configuration updated");
      void queryClient.invalidateQueries({
        queryKey: ["compliance", "admin", "document-types"],
      });
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update requirement configuration",
      );
    },
  });

  const deactivateDocumentTypeMutation = useMutation({
    mutationFn: (code: string) =>
      complianceApi.admin.deactivateDocumentType({
        accessToken: context.accessToken,
        code,
      }),
    onSuccess: () => {
      toast.success("Requirement configuration deactivated");
      void queryClient.invalidateQueries({
        queryKey: ["compliance", "admin", "document-types"],
      });
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to deactivate requirement configuration",
      );
    },
  });

  const reactivateDocumentTypeMutation = useMutation({
    mutationFn: (code: string) =>
      complianceApi.admin.reactivateDocumentType({
        accessToken: context.accessToken,
        code,
      }),
    onSuccess: () => {
      toast.success("Requirement configuration reactivated");
      void queryClient.invalidateQueries({
        queryKey: ["compliance", "admin", "document-types"],
      });
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to reactivate requirement configuration",
      );
    },
  });

  if (!hasReadAccess) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Switch
            checked={search.includeInactive}
            onCheckedChange={(checked) => {
              navigate({
                to: ".",
                search: { ...search, includeInactive: checked },
              });
            }}
            onLabel="Yes"
            offLabel="No"
          />
          <span className="text-sm text-slate-600">
            Include inactive configurations
          </span>
        </div>
        <RefreshIconButton
          label="Refresh requirement configurations"
          onRefresh={() => documentTypesQuery.refetch()}
        />
      </div>

      <DocumentTypesTable
        canWrite={hasWriteAccess}
        rows={documentTypesQuery.data ?? []}
        onCreate={(payload) => createDocumentTypeMutation.mutate(payload)}
        onUpdate={(payload) => updateDocumentTypeMutation.mutate(payload)}
        onDeactivate={(code) => deactivateDocumentTypeMutation.mutate(code)}
        onReactivate={(code) => reactivateDocumentTypeMutation.mutate(code)}
      />
    </div>
  );
}
